import * as React from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Box, Container, Grid2 as Grid, Typography } from '@mui/material'
import TitleBar from 'components/TitleBar'
import DeliveryDatePicker from 'components/DeliveryDatePicker'
import OrderGroup from './components/OrderGroup'
import IngredientGroup from './components/IngredientGroup'
import { ErrorContext } from 'contexts/errorContext'
import { OrdersContext } from 'contexts/ordersContext'
import { Parser } from 'expr-eval'
import useFetchWithAuth from 'hooks/useFetchWithAuth'

/**
 * Makes order groups
 */
const groupOrders = (orders) => {
  const groups = {}

  // Making groups
  orders.forEach((order) => {
    order.items.forEach((item) => {
      const groupLevel1 = item.groups.find((group) => group.level === 1) || { name: 'Others', ordering: Number.MAX_VALUE }
      const groupLevel2 = item.groups.find((group) => group.level === 2) || { name: 'Others', ordering: Number.MAX_VALUE }

      if (!groups[groupLevel1.name]) {
        groups[groupLevel1.name] = { ...groupLevel1, total_items: 0, nested_groups: {} }
      }

      if (!groups[groupLevel1.name].nested_groups[groupLevel2.name]) {
        groups[groupLevel1.name].nested_groups[groupLevel2.name] = { ...groupLevel2, items: {} }
      }

      groups[groupLevel1.name].total_items += item.quantity

      // Grouping items except custom
      if (item.type === 'custom_meal' || item.notes) {
        groups[groupLevel1.name].nested_groups[groupLevel2.name].items['Custom ' + item._id] = {
          ...item,
          customer: order.customerName,
          orderType: order.type,
        }
      } else if (!groups[groupLevel1.name].nested_groups[groupLevel2.name].items[item.name]) {
        groups[groupLevel1.name].nested_groups[groupLevel2.name].items[item.name] = { ...item }
      } else {
        groups[groupLevel1.name].nested_groups[groupLevel2.name].items[item.name].quantity += item.quantity
      }
    })
  })

  // Groups re-defining and sorting
  return Object.values(groups)
    .map((group) => ({
      ...group,
      nested_groups: Object.values(group.nested_groups)
        .map((nestedGroup) => ({
          ...nestedGroup,
          items: Object.values(nestedGroup.items).sort((a, b) => a.ordering - b.ordering),
        }))
        .sort((a, b) => a.ordering - b.ordering),
    }))
    .sort((a, b) => a.ordering - b.ordering)
}

/**
 * Orders component
 */
const Orders = ({ orders }) => {
  const orderGroups = React.useMemo(() => {
    return groupOrders(orders)
  }, [orders])

  return (
    <>
      {orderGroups.map((group) => (
        <OrderGroup key={group.name} group={group} />
      ))}
    </>
  )
}

/**
 * Extract ingredients from orders and convert them into groups
 */
const groupIngredients = (orders, allIngredients) => {
  const allIngredientsMap = new Map(
    allIngredients.map(i => [i.name, i])
  )

  const groups = {
    protein: { name: 'Protein', ingredients: {} },
    carbs: { name: 'Carbs', ingredients: {} },
    veggies: { name: 'Veggies', ingredients: {} },
  }

  const nameMap = {
    'Breaded Haddock': 'Haddock',
    'Taco Beans': 'Bean Salad',
    'Piri Piri Chicken': 'Chicken Thigh',
  }

  const getIngredientName = (name) => nameMap[name] || name

  // Aggregate cooked quantities
  orders.forEach((order) => {
    order.items.forEach((item) => {
      item.ingredients.forEach(({ ingredient, weight }) => {
        if (!groups[ingredient.type] || !weight) return

        const name = getIngredientName(ingredient.name)
        const group = groups[ingredient.type]
        
        if (!group.ingredients[name]) {
          group.ingredients[name] = { ...ingredient, name, cookedQty: 0 }
        }

        group.ingredients[name].cookedQty += weight * item.quantity
      })
    })
  })

  // Splitting Roasted Veggies
  if (groups.veggies.ingredients['Roasted Veggies']) {
    const newIngredients = ['Brussel Sprouts', 'Roasted Carrots', 'Roasted Peppers']

    newIngredients.forEach((name) => {
      if (!groups.veggies.ingredients[name]) {
        const ingredient = allIngredientsMap.get(name)
        if (ingredient) {
          groups.veggies.ingredients[name] = { ...ingredient, cookedQty: 0 }
        }
      }
    })
  }

  // Flatten order ingredients for formula lookup
  const orderIngredientList = Object.values(groups)
    .flatMap((g) => Object.values(g.ingredients))
    .reduce((list, ingredient) => {
      list[ingredient.shortName] = ingredient.cookedQty
      return list
    }, {})

  const parser = new Parser()

  // Calculate raw quantities
  Object.values(groups).forEach((group) => {
    Object.values(group.ingredients).forEach((ingredient) => {
      if (!ingredient.rawFormula) return

      // Replace [tags] with values from orderIngredientList
      const formula = ingredient.rawFormula.replace(
        /\[([^\]]+)\]/g,
        (_, key) =>
          key.toLowerCase() === 'weight'
            ? ingredient.cookedQty
            : orderIngredientList[key] ?? 0
      )

      try {
        ingredient.rawQty = parser.evaluate(formula)
      } catch (err) {
        console.warn(`Error evaluating formula for ${ingredient.name}:`, formula)
      }
    })
  })

  // Remove Roasted Veggies
  delete groups.veggies.ingredients['Roasted Veggies']

  // Return sorted, clean groups
  return Object.values(groups).map((group) => ({
    name: group.name,
    ingredients: Object.values(group.ingredients).sort((a, b) => a.ordering - b.ordering),
  }))
}

/**
 * Ingredients component
 */
const Ingredients = ({ orders }) => {
  const [ingredients, setIngredients] = React.useState([])
  const { showError } = React.useContext(ErrorContext)
  const fetchWithAuth = useFetchWithAuth()

  // Fetches all ingredients
  React.useEffect(() => {
    const fetchIngredients = async () => {
      try {
        const response = await fetchWithAuth('/api/product-ingredients')
        const data = await response.json()
        setIngredients(data)
      } catch (error) {
        showError('Failed to fetch product ingredients. Please try again or refresh the page.')
        console.error(error.message)
      }
    }

    fetchIngredients()
  }, [])

  const ingredientGroups = React.useMemo(() => {
    if (!ingredients.length || !orders.length) return []
    return groupIngredients(orders, ingredients)
  }, [orders, ingredients])

  return (
    <>
      <Typography variant="h6" component="h6" sx={{ mt: 3 }}>
        Ingredients
      </Typography>
      {ingredientGroups.map((group) => (
        <IngredientGroup key={group.name} group={group} />
      ))}
    </>
  )
}

/**
 * Summary component for displaying order groups and ingredients
 */
const Summary = () => {
  const { orders, fetchOrders } = React.useContext(OrdersContext)
  const { deliveryDate } = useParams()

  // Fetching orders
  React.useEffect(() => {
    fetchOrders(deliveryDate)
  }, [deliveryDate, fetchOrders])

  return (
    <Box>
      <TitleBar title="Summary" actions={[<DeliveryDatePicker />]} />
      <Container maxWidth="xl">
        {orders.length > 0 ? (
          <Grid container spacing={6} sx={{ my: 3 }}>
            {/* Orders column */}
            <Grid key="orders-section" size={8}>
              <Orders orders={orders} />
            </Grid>

            {/* Ingredients column */}
            <Grid key="ingredients-section" size={4}>
              <Ingredients orders={orders} />
            </Grid>
          </Grid>
        ) : (
          <Alert severity="warning" sx={{ my: 6, border: 1, borderColor: 'warning.light' }}>
            No orders found.
          </Alert>
        )}
      </Container>
    </Box>
  )
}

export default Summary
